import classNames from 'classnames';
import { Form, Formik, FormikConfig, useField, useFormikContext } from 'formik';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { graphqlErrorCode } from '../../../utils/graphqlErrors';
import { Button, CircleSpinner } from '../../../components/Button';
import { Modal } from '../../../components/Modal';
import { CheckBox } from '../../../components/inputs/CheckBox';
import { FormikInput } from '../../../components/formik/FormInput';
import useOpen from '../../../hooks/useOpen';
import { T, useT } from '../../../translation/src';
import { useOrganisationBillingQuery, useOrganisationLinkBillingMutation, useOrganisationPurchaseCreditsMutation } from '../../../types/graphqlTypes';
import { POINTORAMA_TERMS_URL } from '../../../utils/constants';
import { formatPrice } from '../../../utils/formatPrice';
import { BillingInfoCard, BillingInfoModal } from '../BillingInfoCard';
import { ContactSalesModal } from '../ContactSalesModal';
import { useOrderConfirmation } from '../OrderConfirmationModal';

// Whatever is on screen: the organisation (its balance), and the history when it is open.
const REFETCH_QUERIES = 'active' as const;

const useTranslations = () => {
  const numberOfProjects = useT('quantity', { swc: true });
  const failed = useT('could not place the order', { swc: true });
  return { numberOfProjects, failed };
};

interface OrderFormValues {
  amount: number;
  // The terms, accepted explicitly: the order waits until they are.
  agreeTerms: boolean;
}

// The order lines and the submit label both follow the amount being typed,
// so they read it from the form rather than taking it as a prop.
const useOrderAmount = () => {
  const { values } = useFormikContext<OrderFormValues>();
  return Math.max(0, Math.floor(Number(values.amount) || 0));
};

// The terms & conditions, agreed to by ticking — not by reading a notice. A plain checkbox in a label, not
// the form one: that one reserves a row for an error under itself, which pushed the footer away.
const TermsCheckBox: React.FC = () => {
  const [field] = useField<boolean>({ name: 'agreeTerms', type: 'checkbox' });
  return (
    <label className={classNames('flex cursor-pointer items-center gap-2 rounded-md border p-3 text-xs', field.checked ? 'border-gray-300 dark:border-gray-700' : 'border-warning-yellow-500')}>
      <CheckBox name={field.name} checked={!!field.checked} onChange={field.onChange} onBlur={field.onBlur} />
      <span>
        <T _str="I agree to the" />{' '}
        <a href={POINTORAMA_TERMS_URL} target="_blank" rel="noreferrer" className="text-french-violet-600 underline dark:text-french-violet-400">
          <T _str="terms & conditions" />
        </a>{' '}
        <T _str="for this purchase" />.
      </span>
    </label>
  );
};

const OrderSummary: React.FC<{ unitPrice: number }> = ({ unitPrice }) => {
  const amount = useOrderAmount();
  return (
    <div className="flex flex-col text-sm">
      <div className="flex justify-between gap-4 py-0.5">
        <span>
          <T _str="price per project" swc />
        </span>
        <span>{formatPrice(unitPrice)}</span>
      </div>
      <div className="flex justify-between gap-4 py-0.5">
        <span>
          <T _str="quantity" swc />
        </span>
        <span>{amount}</span>
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-4 border-t border-gray-200 pt-2 dark:border-gray-700">
        <span className="font-semibold uppercase">
          <T _str="total" swc />
        </span>
        <span className="text-lg font-semibold">{formatPrice(unitPrice * amount)}</span>
      </div>
      <span className="text-right text-xs text-gray-500">
        <T _str="excl. VAT" />
      </span>
    </div>
  );
};

const BuyButton: React.FC<{ unitPrice: number; loading?: boolean; disabled?: boolean }> = ({ unitPrice, loading, disabled }) => {
  const amount = useOrderAmount();
  const { values } = useFormikContext<OrderFormValues>();
  return (
    <Button variant="primary" type="submit" loading={loading} disabled={disabled || !values.agreeTerms}>
      <T _str="buy projects" swc /> - {formatPrice(unitPrice * amount)}
    </Button>
  );
};

// In place of the order while the organisation has no customer in Odoo: busy while it is being linked, and
// when there is nothing to link, the way to sales.
const NotLinked: React.FC<{ pending: boolean; onClose: () => void; onContactSales: () => void }> = ({ pending, onClose, onContactSales }) => (
  <>
    <Modal.Header className="mb-2!" onClose={onClose}>
      <T _str="buy projects" swc />
    </Modal.Header>
    {pending ? (
      <Modal.Body className="flex h-40 items-center justify-center">
        <CircleSpinner className="h-6 w-6" />
      </Modal.Body>
    ) : (
      <>
        <Modal.Body className="flex flex-col gap-2 text-sm">
          <span className="font-semibold">
            <T _str="Your organisation is not set up for ordering yet." />
          </span>
          <span className="text-gray-500">
            <T _str="Contact sales and we will get you started, so you can buy projects here." />
          </span>
        </Modal.Body>
        <Modal.Footer className="justify-between">
          <Button variant="secondary" onClick={onClose}>
            <T _str="cancel" swc />
          </Button>
          <Button variant="primary" onClick={onContactSales}>
            <T _str="contact sales" swc />
          </Button>
        </Modal.Footer>
      </>
    )}
  </>
);

export interface BuyCreditsModalProps {
  onClose: () => void;
  open?: boolean;
  organisationId: string;
  projectUnitPrice: number;
  // The bounds of one order, as the server enforces them.
  minAmount: number;
  maxAmount: number;
}
const BuyCreditsModal_: React.FC2<BuyCreditsModalProps> = ({ onClose, open, organisationId, projectUnitPrice, minAmount, maxAmount }) => {
  const [purchaseCredits, { loading }] = useOrganisationPurchaseCreditsMutation({ refetchQueries: REFETCH_QUERIES });
  const translations = useTranslations();
  const { showToast } = useToastNotifications();
  const { show: showConfirmation } = useOrderConfirmation();
  // No order without billing details: the card asks for them and the buy button waits. Only a definite
  // "incomplete" blocks — Odoo being off or unreachable does not, and the order then shows without the card.
  const { data: billingData, error: billingError, refetch: refetchBilling } = useOrganisationBillingQuery({ variables: { organisationId }, skip: !open, fetchPolicy: 'cache-and-network' });
  // A failed lookup leaves the last result in place, which may be outdated: show nothing instead.
  const billing = billingError ? undefined : billingData?.organisationBilling;
  const billingMissing = !!billing?.enabled && !billing.complete;
  const { open: billingModalOpen, onOpen: openBillingModal, onClose: closeBillingModal } = useOpen();

  // An organisation without a customer in Odoo is linked to it first, once per opening, by itself: to the one
  // it has there, or a new one when sales knows it by its registration lead. When there is nothing to link,
  // there is no order — the customer is pointed to sales instead.
  const [linkBilling, { loading: linking, called: linkTried, reset: resetLink }] = useOrganisationLinkBillingMutation();
  const unlinked = !!billing?.enabled && !billing.linked;
  useEffect(() => {
    if (!open) {
      if (!linkTried) return;
      // Once the window has faded out: forgetting the attempt while it still shows would flash the spinner.
      const timeout = setTimeout(resetLink, 300);
      return () => clearTimeout(timeout);
    }
    // The answer updates the cached billing by itself; a failure leaves it unlinked, which reads as "contact sales".
    if (unlinked && !linkTried) linkBilling({ variables: { organisationId } }).catch(() => undefined);
  }, [linkBilling, linkTried, open, organisationId, resetLink, unlinked]);
  const linkPending = unlinked && (!linkTried || linking);
  const linkFailed = unlinked && linkTried && !linking;
  const { open: contactSalesOpen, onOpen: openContactSales, onClose: closeContactSales } = useOpen();
  // Contact sales takes the place of the order: one window at a time.
  const onContactSales = useCallback(() => {
    onClose();
    openContactSales();
  }, [onClose, openContactSales]);

  const onSubmit: FormikConfig<OrderFormValues>['onSubmit'] = useCallback(
    async (values) => {
      // Never without the terms ticked, whatever submitted the form.
      if (!values.agreeTerms) return;
      const amount = Number(values.amount);
      try {
        await purchaseCredits({ variables: { organisationId, amount } });
      } catch (e) {
        // The details went missing since the dialog opened (edited in Odoo): the card asks for them again.
        if (graphqlErrorCode(e) === 'BILLING_INFO_MISSING') await refetchBilling();
        else showToast({ title: translations.failed, text: e instanceof Error ? e.message : String(e), type: ToastType.ERROR });
        return;
      }
      // The window unmounts its form as it closes: nothing to reset. The confirmation takes its place.
      onClose();
      showConfirmation({
        organisationId,
        what: <T _str="{count} projects" args={{ count: amount }} />,
        ready: <T _str="Your {count} projects are on your balance. Use them for your next uploads." args={{ count: amount }} />,
        totalCents: projectUnitPrice * amount,
        invoiceEmail: billing?.info?.email,
      });
    },
    [billing?.info?.email, onClose, organisationId, projectUnitPrice, purchaseCredits, refetchBilling, showConfirmation, showToast, translations.failed],
  );

  const formik: FormikConfig<OrderFormValues> = useMemo(() => ({ initialValues: { amount: minAmount, agreeTerms: false }, onSubmit }), [minAmount, onSubmit]);

  return (
    <>
      <Modal.Container open={open} onClose={onClose} panelClassName="w-full max-w-lg">
        {linkPending || linkFailed ? (
          <NotLinked pending={linkPending} onClose={onClose} onContactSales={onContactSales} />
        ) : (
          <>
            <Formik<OrderFormValues> {...formik}>
              <Form className="flex w-full flex-col">
                <Modal.Header className="mb-2!" onClose={onClose}>
                  <T _str="buy projects" swc />
                </Modal.Header>
                <Modal.Body className="flex flex-col gap-4">
                  <span className="text-sm text-gray-500">
                    <T _str="Add projects to your balance. They are available immediately and will be invoiced afterwards." />
                  </span>
                  <div className="flex flex-col">
                    <FormikInput type="number" min={minAmount} max={maxAmount} autoFocus name="amount" label={translations.numberOfProjects} />
                    {/* FormInput always reserves an (empty here) error row — pull the hint back up under the control. */}
                    <span className="-mt-4 text-xs text-gray-500">
                      <T _str="The minimum order is {minAmount} projects." args={{ minAmount }} />
                    </span>
                  </div>
                  <OrderSummary unitPrice={projectUnitPrice} />
                  <BillingInfoCard billing={billing} onEdit={openBillingModal} />
                  <TermsCheckBox />
                </Modal.Body>
                <Modal.Footer className="justify-between">
                  <Button variant="secondary" onClick={onClose} disabled={loading}>
                    <T _str="cancel" swc />
                  </Button>
                  <BuyButton unitPrice={projectUnitPrice} loading={loading} disabled={billingMissing} />
                </Modal.Footer>
              </Form>
            </Formik>
            {/* Outside the order's form: a submit in the overlay must not bubble into it. */}
            <BillingInfoModal open={billingModalOpen} onClose={closeBillingModal} organisationId={organisationId} info={billing?.info} />
          </>
        )}
      </Modal.Container>
      {/* Beside the order's window, not in it: it opens as that one closes. */}
      <ContactSalesModal open={contactSalesOpen} onClose={closeContactSales} organisationId={organisationId} />
    </>
  );
};

export const BuyCreditsModal = memo(BuyCreditsModal_);
